import React, { useCallback, useMemo } from 'react';
import { Column } from 'react-table';
import cn from 'classnames';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { ClientExploreTiktokAudiosTableRow } from '../../../../../TikTok.types';
import Skeleton from '../../../../../../../ui/Skeleton/Skeleton';
import { formatNumberToKNotation, roundTo2Dp } from '../../../../../../../helpers';
import TruncatedTextCell from '../../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import BarsSmallChart from '../../../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import styles from './ClientTiktokAudiosTable.module.css';
import { OrderingValues, TrndsClientTiktokAudioStats } from '@round/api';
import Image from '../../../../../../../ui/Image/Image';
import TiktokAudioBadge from '../../../../components/TiktokAudioBadge/TiktokAudioBadge';

export type ClientTiktokAudiosTableOrderingValues = OrderingValues<
    keyof Pick<
        TrndsClientTiktokAudioStats,
        | 'video_count'
        | 'video_count_daily_change'
        | 'video_count_daily_change_relative'
        | 'video_count_weekly_change'
        | 'video_count_weekly_change_relative'
    >
>;

const isOrderingKey = (
    value: string
): value is keyof Pick<
    TrndsClientTiktokAudioStats,
    | 'video_count'
    | 'video_count_daily_change'
    | 'video_count_daily_change_relative'
    | 'video_count_weekly_change'
    | 'video_count_weekly_change_relative'
> =>
    value === 'video_count' ||
    value === 'video_count_daily_change' ||
    value === 'video_count_daily_change_relative' ||
    value === 'video_count_weekly_change' ||
    value === 'video_count_weekly_change_relative';

type TiktokAudiosTableProps = Omit<
    PaginationTableProps<ClientExploreTiktokAudiosTableRow>,
    'columns' | 'orderBy' | 'onOrderByChange'
> & {
    orderByDesc: string | undefined;
};

const ClientTiktokAudiosTable = ({
    pageSize,
    setPageSize,
    setPage,
    page,
    loading,
    data,
    noDataLabel,
    count,
    orderByDesc,
    onPaginationChange,
    onRowClick,
}: TiktokAudiosTableProps) => {
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (orderByDesc?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [orderByDesc]
    );

    const columns: Column<ClientExploreTiktokAudiosTableRow>[] = useMemo(
        () => [
            {
                Header: 'Sound',
                accessor: 'title',
                Cell: ({ row: { original } }) => (
                    <TiktokAudioBadge
                        loading={loading}
                        audio={{
                            tiktokId: original.tiktok_id,
                            isOriginal: original.is_original,
                            imageUrl: original.image_url,
                            title: original.title,
                            audioPlayUrl: original.audioUrl,
                        }}
                    />
                ),
            },
            {
                Header: 'Author',
                accessor: 'author_name',
                Cell: ({ value }) =>
                    loading ? (
                        <Skeleton width="75%" />
                    ) : (
                        <TruncatedTextCell className={styles.authorName}>{value}</TruncatedTextCell>
                    ),
            },
            {
                Header: 'Video',
                accessor: 'video_count',
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton width="75%" />;
                    }

                    const orderByValue = orderByDesc?.charAt(0) === '-' ? orderByDesc.slice(1) : orderByDesc;
                    const badgeValue =
                        orderByValue && isOrderingKey(orderByValue) && orderByValue !== 'video_count'
                            ? original[orderByValue]
                            : null;
                    const badgeValueAsNumber =
                        typeof badgeValue === 'number'
                            ? badgeValue
                            : typeof badgeValue === 'string'
                              ? parseFloat(badgeValue)
                              : null;

                    return (
                        <>
                            <span className={styles.videoCount}>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValueAsNumber === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValueAsNumber >= 0,
                                        [styles.decreasing]: badgeValueAsNumber < 0,
                                    })}
                                >
                                    {badgeValueAsNumber > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValueAsNumber)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'dailyGrowth',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.dailyGrowth}>
                            <BarsSmallChart
                                values={value.slice(-7)}
                                color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                            />
                        </div>
                    );
                },
            },
            {
                Header: 'Linked Song',
                accessor: 'linkedSongs',
                Cell: ({ value }) => {
                    const [song] = value ?? [];
                    if (loading) {
                        return <Skeleton />;
                    }
                    if (!song) {
                        return <span>No song linked</span>;
                    }
                    return (
                        <div className={styles.songContainer}>
                            <Image src={song.image ?? ''} className={styles.songImage} />
                            <div className={styles.songDetails}>
                                <p className={styles.songTitle}>{song.title}</p>
                                <p className={styles.songSubtitle}>{song.artists.map((a) => a.name).join(', ')}</p>
                            </div>
                        </div>
                    );
                },
            },
        ],
        [formatVideoCountBadgeValue, loading, orderByDesc]
    );

    return (
        <div data-test-id="audios-table">
            <PaginationTable
                tableClassName={styles.table}
                tbodyClassName={styles.tbody}
                loading={loading}
                data={data}
                noDataLabel={noDataLabel}
                columns={columns}
                count={count}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onPaginationChange={onPaginationChange}
                onRowClick={onRowClick}
            />
        </div>
    );
};

export default ClientTiktokAudiosTable;
